<template>
    <div class="flex flex-col">
        <!-- Filters -->
        <form class="mb-8" @submit.prevent="submitFilters">
            <div
                class="grid grid-cols-1 items-end gap-3 lg:grid-cols-[1fr_auto_auto_auto]"
            >
                <!-- Search -->
                <div class="relative">
                    <input
                        v-model="searchInput"
                        type="text"
                        placeholder="Поиск..."
                        class="h-11 w-full rounded-lg border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:border-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                    <svg
                        class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>

                <!-- Dates + Buttons row (stacks below search on <lg) -->
                <div
                    class="col-span-1 grid grid-cols-[1fr_1fr] items-end gap-3 sm:flex sm:gap-3 lg:contents"
                >
                    <!-- Date From -->
                    <div class="flex flex-col">
                        <label class="mb-1 text-xs text-gray-400">
                            Дата от
                        </label>
                        <input
                            ref="dateFromEl"
                            type="text"
                            placeholder="ГГГГ-ММ-ДД"
                            class="h-11 w-full cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:border-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-accent sm:w-36"
                        />
                    </div>

                    <!-- Date To -->
                    <div class="flex flex-col">
                        <label class="mb-1 text-xs text-gray-400">
                            Дата до
                        </label>
                        <input
                            ref="dateToEl"
                            type="text"
                            placeholder="ГГГГ-ММ-ДД"
                            class="h-11 w-full cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:border-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-accent sm:w-36"
                        />
                    </div>

                    <!-- Buttons -->
                    <div class="col-span-2 flex gap-2 sm:col-span-1">
                        <button
                            v-if="hasActiveFilters"
                            type="button"
                            class="h-11 rounded-lg border border-gray-200 px-4 text-sm text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-700"
                            @click="clearFilters"
                        >
                            Сбросить
                        </button>
                        <button
                            type="submit"
                            class="h-11 rounded-lg border border-accent px-5 text-sm text-accent transition-colors hover:bg-accent hover:text-white"
                        >
                            Найти
                        </button>
                    </div>
                </div>
            </div>
        </form>

        <!-- Articles List -->
        <div v-if="articlesArray.length > 0" class="flex flex-col">
            <article
                v-for="article in articlesArray"
                :key="article.id"
                class="group relative border-b border-black/10 py-8 last:border-0"
            >
                <div class="grid grid-cols-12 items-start gap-6">
                    <!-- Content Column -->
                    <div
                        class="order-2 col-span-12 flex min-w-0 flex-col sm:order-1 sm:col-span-7 xl:col-span-8"
                    >
                        <div
                            class="mb-3 flex min-w-0 flex-row items-baseline justify-between"
                        >
                            <a
                                :href="article.categoryUrl"
                                class="text-xs font-bold uppercase tracking-wider text-accent transition-all duration-300 hover:text-accent/80 md:text-sm"
                            >
                                {{ article.categoryTitle }}
                            </a>
                            <span
                                class="ml-4 whitespace-nowrap text-xs text-gray-400"
                            >
                                {{ article.publishAt }}
                            </span>
                        </div>

                        <a
                            :href="article.url"
                            :hreflang="article.hreflang"
                            class="block"
                        >
                            <h3
                                class="mb-3 font-secondary-heading text-lg font-medium leading-snug text-black transition-colors duration-200 group-hover:text-accent md:text-xl xl:text-2xl"
                            >
                                {{ article.title }}
                            </h3>

                            <p
                                v-if="article.content"
                                class="hidden text-sm leading-relaxed text-gray-600 md:text-base xl:line-clamp-3"
                            >
                                {{ truncate(article.content, 120) }}
                            </p>
                        </a>
                    </div>

                    <!-- Image Column -->
                    <div
                        v-if="article.photo"
                        class="order-1 col-span-12 sm:order-2 sm:col-span-5 xl:col-span-4"
                    >
                        <a
                            :href="article.url"
                            class="relative block aspect-video overflow-hidden rounded-md bg-gray-100 sm:aspect-[4/3]"
                        >
                            <img
                                :src="article.photo"
                                :alt="article.title || 'Article Image'"
                                loading="lazy"
                                class="absolute inset-0 h-full w-full transform object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                            />
                        </a>
                    </div>
                </div>
            </article>
        </div>

        <!-- No Results -->
        <div v-else class="py-16 text-center text-gray-400">
            <p class="text-lg">Ничего не найдено</p>
        </div>

        <!-- Pagination -->
        <nav
            v-if="lastPage > 1"
            class="mt-10 flex items-center justify-center gap-1"
            aria-label="News pagination"
        >
            <button
                type="button"
                class="px-3 py-2 text-sm font-accent text-gray-500 transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-30"
                :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)"
            >
                Назад
            </button>

            <button
                v-for="page in pages"
                :key="page"
                type="button"
                class="mx-0.5 flex h-9 w-9 items-center justify-center border text-sm transition-colors"
                :class="
                    page === currentPage
                        ? 'border-accent bg-accent text-white'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-accent hover:text-accent'
                "
                @click="goToPage(page)"
            >
                {{ page }}
            </button>

            <button
                type="button"
                class="px-3 py-2 text-sm font-accent text-gray-500 transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-30"
                :disabled="currentPage >= lastPage"
                @click="goToPage(currentPage + 1)"
            >
                Вперёд
            </button>
        </nav>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import flatpickr from "flatpickr";
import { Russian } from "flatpickr/dist/l10n/ru.js";
import "flatpickr/dist/flatpickr.min.css";
import type { Instance } from "flatpickr/dist/types/instance";

interface ArticleItem {
    id: string;
    url: string;
    hreflang: string;
    publishAt: string;
    title: string;
    content: string;
    photo: string;
    categoryUrl: string;
    categoryTitle: string;
}

const props = defineProps<{
    articles: Record<string, ArticleItem>;
    currentPage: number;
    lastPage: number;
    total: number;
    search: string;
    dateFrom: string;
    dateTo: string;
}>();

const searchInput = ref(props.search);
const dateFromInput = ref(props.dateFrom);
const dateToInput = ref(props.dateTo);

const dateFromEl = ref<HTMLInputElement>();
const dateToEl = ref<HTMLInputElement>();

let fpFrom: Instance | null = null;
let fpTo: Instance | null = null;

const flatpickrConfig = (onChange: (val: string) => void): flatpickr.Options.Options => ({
    locale: Russian,
    dateFormat: "Y-m-d",
    allowInput: true,
    maxDate: "today",
    onChange: (_dates, dateStr) => {
        onChange(dateStr);
    },
});

onMounted(() => {
    if (dateFromEl.value) {
        fpFrom = flatpickr(dateFromEl.value, {
            ...flatpickrConfig((val) => (dateFromInput.value = val)),
            defaultDate: props.dateFrom || undefined,
        });
    }
    if (dateToEl.value) {
        fpTo = flatpickr(dateToEl.value, {
            ...flatpickrConfig((val) => (dateToInput.value = val)),
            defaultDate: props.dateTo || undefined,
        });
    }
});

onBeforeUnmount(() => {
    fpFrom?.destroy();
    fpTo?.destroy();
});

const currentPage = computed(() => props.currentPage);
const lastPage = computed(() => props.lastPage);

const articlesArray = computed<ArticleItem[]>(() => Object.values(props.articles));

const hasActiveFilters = computed(
    () =>
        searchInput.value.trim() !== "" ||
        dateFromInput.value !== "" ||
        dateToInput.value !== "",
);

const pages = computed<number[]>(() =>
    Array.from({ length: lastPage.value }, (_v, index) => index + 1),
);

const truncate = (text: string, length: number): string => {
    if (text.length <= length) return text;
    return text.substring(0, length).trimEnd() + "...";
};

const buildFilteredUrl = (): URL => {
    const url = new URL(window.location.href);

    const search = searchInput.value.trim();
    const dateFrom = dateFromInput.value;
    const dateTo = dateToInput.value;

    if (search) {
        url.searchParams.set("search", search);
    } else {
        url.searchParams.delete("search");
    }

    if (dateFrom) {
        url.searchParams.set("date_from", dateFrom);
    } else {
        url.searchParams.delete("date_from");
    }

    if (dateTo) {
        url.searchParams.set("date_to", dateTo);
    } else {
        url.searchParams.delete("date_to");
    }

    return url;
};

const submitFilters = () => {
    const url = buildFilteredUrl();
    url.searchParams.delete("page");
    window.location.href = url.toString();
};

const clearFilters = () => {
    searchInput.value = "";
    dateFromInput.value = "";
    dateToInput.value = "";
    fpFrom?.clear();
    fpTo?.clear();
    submitFilters();
};

const goToPage = (page: number) => {
    if (page < 1 || page > lastPage.value) return;

    const url = buildFilteredUrl();
    url.searchParams.set("page", String(page));
    window.location.href = url.toString();
};
</script>
